import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Label, Input } from "reactstrap";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const UpdateBranch = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Update Branch", link: "#" },
  ]);
  const navigate = useNavigate();
  const { id } = useParams();

  // State and city options
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  const [formData, setFormData] = useState({
    branch_code: "",
    name: "",
    state: "",
    city: "",
    address: "",
    email: "",
    phone: "",
  });

  // Fetch states, cities, and branch data
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await apiClient.get("/branches/states");
        setStates(res.data.response || []);
      } catch (err) {
        console.error(err);
      }
    };
    const fetchCities = async () => {
      try {
        const res = await apiClient.get("/branches/cities");
        setCities(res.data.response || []);
      } catch (err) {
        console.error(err);
      }
    };
    const fetchBranch = async () => {
      try {
        const res = await apiClient.get(`/branches/getbranch/${id}`);
        const data = res.data.response;
        setFormData({
          branch_code: data.branch_code || "",
          name: data.name || "",
          state: data.state_id || "",
          city: data.city_id || "",
          address: data.address || "",
          email: data.email || "",
          phone: data.phone || "",
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchStates();
    fetchCities();
    fetchBranch();
  }, [id]);

  // Update filtered cities when state changes
  useEffect(() => {
    if (formData.state) {
      const filtered = cities.filter((c) => c.state_id === formData.state);
      setFilteredCities(filtered);
      // Reset city if not in filtered
      if (formData.city && !filtered.some((c) => c.id === formData.city)) {
        setFormData((prev) => ({ ...prev, city: "" }));
      }
    } else {
      setFilteredCities([]);
      setFormData((prev) => ({ ...prev, city: "" }));
    }
  }, [formData.state, cities, formData.city]);

  const stateOptions = [
    { label: "Select State", value: "" },
    ...states.map((s) => ({ label: s.state_name, value: s.id })),
  ];
  const cityOptions = [
    { label: "Select City", value: "" },
    ...filteredCities.map((c) => ({ label: c.city_name, value: c.id })),
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#007bff"
        : state.isFocused
        ? "#f0f0f0"
        : "white",
      color: state.isSelected ? "white" : "black",
    }),
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : "",
    }));
  };

  const handleReset = () => {
    setFormData({
      branch_code: "",
      name: "",
      state: "",
      city: "",
      address: "",
      email: "",
      phone: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await apiClient.put(
      `/branches/updateBranch/${id}`,
      formData
    );
    await showAlert({
      title: "Branch Update!",
      text: response.data.message,
      icon: response.data.status,
      goBack: false,
      timer: 5000,
    });
    if (response.data.status === "success") {
      navigate(-1);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Update Branch" breadcrumbItems={breadcrumbItems} />
        <form onSubmit={handleSubmit}>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title d-flex justify-content-between align-items-center">
                    Branch Update Form
                    <i
                      className="ri-arrow-left-line"
                      style={{ fontSize: "1.25rem", cursor: "pointer" }}
                      onClick={() => navigate(-1)}
                    ></i>
                  </h4>
                  <hr className="bg-primary" />
                  <Row>
                    <Col lg="1" className="mt-3">
                      <Label className="form-label">Branch Code</Label>
                      <Input
                        type="text"
                        id="branch_code"
                        name="branch_code"
                        placeholder="Code"
                        value={formData.branch_code}
                        onChange={handleInputChange}
                        maxLength={10}
                        required
                      />
                    </Col>
                    <Col lg="3" className="mt-3">
                      <Label className="form-label">Branch Name</Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Enter Branch Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={100}
                        required
                      />
                    </Col>
                    <Col lg="4" className="mt-3">
                      <Label className="form-label">State</Label>
                      <Select
                        id="state"
                        name="state"
                        value={stateOptions.find(
                          (option) => option.value === formData.state
                        )}
                        onChange={(selected) =>
                          handleSelectChange(selected, "state")
                        }
                        options={stateOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                      />
                    </Col>
                    <Col lg="4" className="mt-3">
                      <Label className="form-label">City</Label>
                      <Select
                        id="city"
                        name="city"
                        value={cityOptions.find(
                          (option) => option.value === formData.city
                        )}
                        onChange={(selected) =>
                          handleSelectChange(selected, "city")
                        }
                        options={cityOptions}
                        styles={customStyles}
                        classNamePrefix="select2-selection form-control"
                        required
                        isDisabled={!formData.state}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="4" className="mt-3">
                      <Label className="form-label">Address</Label>
                      <Input
                        type="text"
                        id="address"
                        name="address"
                        placeholder="Enter Address"
                        value={formData.address}
                        onChange={handleInputChange}
                        maxLength={100}
                      />
                    </Col>
                    <Col lg="4" className="mt-3">
                      <Label className="form-label">Email</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        maxLength={100}
                      />
                    </Col>
                    <Col lg="4" className="mt-3">
                      <Label className="form-label">Phone</Label>
                      <Input
                        type="text"
                        id="phone"
                        name="phone"
                        placeholder="Enter Phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        maxLength={20}
                      />
                    </Col>
                  </Row>
                  <div className="row d-flex justify-content-end">
                    <div className="col-md-2 col-6">
                      <button
                        type="button"
                        className="btn btn-secondary mt-4 w-100"
                        onClick={handleReset}
                      >
                        Reset
                      </button>
                    </div>
                    <div className="col-md-2 col-6">
                      <button
                        type="submit"
                        className="btn btn-success mt-4 w-100"
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
};

export default UpdateBranch;
